/**
 * Écrans publics (rétrocompatibilité v2) : location du club-house à la
 * journée ou demi-journée avec calendrier des disponibilités, demandes
 * d'animation et d'initiation ; écran comité de traitement des demandes.
 */
import React, { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { Spinner, ErrorBox, Badge, useApi, Intro, OfflineNote, IconeCorbeille, IconeCrayon, IconeLoupe } from '../components/common.jsx';
import { fmtCHF, fmtDate } from '../lib/format.js';

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/**
 * Calendrier mensuel unifié de tous les formulaires.
 *   - bloquees     : dates occupées (barrées, non cliquables) ;
 *   - disponibles  : si fourni, seules ces dates sont proposées (cours) ;
 *   - multi        : sélection multiple (selected = Set) ou simple (string) ;
 *   - legende      : texte d'aide sous le calendrier.
 */
export function MiniCalendrier({ bloquees = new Set(), disponibles = null, selected, onSelect, multi = false, legende }) {
  const [mois, setMois] = useState(() => new Date().toISOString().slice(0, 7));
  const [y, m] = mois.split('-').map(Number);
  const premierJour = new Date(y, m - 1, 1).getDay() || 7;
  const nbJours = new Date(y, m, 0).getDate();
  const aujourdHui = new Date().toISOString().slice(0, 10);
  const nav = (d) => {
    const dt = new Date(y, m - 1 + d, 1);
    setMois(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`);
  };
  const estSelectionnee = (iso) => (multi ? selected instanceof Set && selected.has(iso) : selected === iso);
  const cells = [];
  for (let i = 1; i < premierJour; i++) cells.push(<span key={'v' + i} />);
  for (let j = 1; j <= nbJours; j++) {
    const iso = `${mois}-${String(j).padStart(2, '0')}`;
    const passe = iso < aujourdHui;
    const bloquee = bloquees.has(iso);
    const proposee = disponibles === null || disponibles.has(iso);
    const inactive = passe || bloquee || !proposee;
    // Code couleur : violet = date possible, gris = occupée/bloquée/passée.
    cells.push(
      <button type="button" key={iso} disabled={inactive}
        onClick={() => onSelect(iso)}
        className={`adlt-cal-cell btn btn-sm ${estSelectionnee(iso) ? 'adlt-cell-choisie' : inactive ? 'adlt-cell-grise' : 'adlt-cell-possible'}`}>
        {j}
      </button>
    );
  }
  return (
    <div className="adlt-minical">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => nav(-1)}>‹</button>
        <strong className="text-capitalize">{MOIS[m - 1]} {y}</strong>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => nav(1)}>›</button>
      </div>
      <div className="d-grid mb-1" style={{ gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
        {['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'].map((d) => <small key={d} className="text-center text-muted fw-bold">{d}</small>)}
        {cells}
      </div>
      <small className="text-muted">{legende || 'En violet : dates possibles · en gris : dates occupées ou indisponibles.'}</small>
    </div>
  );
}

/** Carte d'étape numérotée — design commun des formulaires. */
export function Etape({ n, titre, children }) {
  return (
    <div className="card adlt-etape mb-3">
      <div className="card-body">
        <h5 className="d-flex align-items-center gap-2 mb-3">
          <span className="adlt-etape-num">{n}</span>{titre}
        </h5>
        {children}
      </div>
    </div>
  );
}

/** Location publique du club-house : journée / demi-journée. */
export function ClubhousePublic() {
  const [dispos, setDispos] = useState(null);
  // Deux erreurs distinctes : l'échec de chargement remplace l'écran, l'échec
  // de saisie s'affiche dans le formulaire. Les confondre faisait disparaître
  // le formulaire — et toute la saisie — au moindre refus de soumission.
  const [erreurChargement, setErreurChargement] = useState('');
  const [erreur, setErreur] = useState('');
  const [date, setDate] = useState(null);
  const [formule, setFormule] = useState('journee');
  const [cleEtat, setCleEtat] = useState('besoin');
  const [etat, setEtat] = useState('saisie');

  useEffect(() => {
    let annule = false;
    const du = new Date().toISOString().slice(0, 10);
    const au = new Date(Date.now() + 120 * 86400000).toISOString().slice(0, 10);
    api(`lieux/0/disponibilites?du=${du}&au=${au}`).then((r) => {
      if (annule) return;
      if (r.ok) setDispos(r.data); else setErreurChargement(r.error);
    });
    return () => { annule = true; };
  }, []);

  if (erreurChargement) return <ErrorBox message={erreurChargement} />;
  if (!dispos) return <Spinner />;
  const connecte = dispos.connecte;

  async function submit(e) {
    e.preventDefault();
    if (!date) { setErreur('Sélectionnez une date disponible dans le calendrier.'); return; }
    setErreur(''); setEtat('envoi');
    const f = e.target;
    const r = await api('reservations/publique', {
      method: 'POST',
      body: {
        lieu_id: dispos.lieu_id, date, formule,
        prenom: f.prenom?.value, nom: f.nom?.value, email: f.email?.value,
        telephone: f.telephone.value, adresse: f.adresse.value,
        npa: f.npa.value, localite: f.localite.value,
        nb_personnes: f.nb.value, motif: f.motif.value,
        cle: cleEtat,
        site_web: f.site_web.value,
      },
    });
    if (r.ok) setEtat('envoye');
    else { setEtat('saisie'); setErreur(r.error); }
  }

  if (etat === 'envoye') {
    return <div className="alert alert-success">Merci ! Votre demande de location a bien été transmise.
      Vous recevrez un courriel dès la décision du comité.</div>;
  }

  return (
    <form onSubmit={submit}>
      <h2 className="h4 mb-3">Louer le club-house</h2>
      <input name="site_web" tabIndex="-1" autoComplete="off" style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true" />
      <div className="row g-4">
        <div className="col-lg-5 order-lg-2">
          <div className="card adlt-etape h-100"><div className="card-body">
            <h5>1. Choisissez une date</h5>
            <MiniCalendrier bloquees={new Set(dispos.bloquees)} selected={date} onSelect={setDate} />
            {date && <div className="alert alert-primary py-2 mt-2 mb-0">Date choisie : <strong>{fmtDate(date)}</strong></div>}
          </div></div>
        </div>
        <div className="col-lg-7 order-lg-1">
          <div className="card adlt-etape mb-3"><div className="card-body">
            <h5>2. Formule</h5>
            <div className="adlt-choix">
              {Object.entries(dispos.formules).map(([cle, fm]) => (
                <button type="button" key={cle}
                  className={`btn ${formule === cle ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFormule(cle)}>
                  {fm.libelle}<br /><strong>{fmtCHF(fm.prix)}</strong>
                </button>
              ))}
            </div>
          </div></div>
          <div className="card adlt-etape"><div className="card-body">
            <h5>3. Vos coordonnées</h5>
            {connecte ? (
              <p className="text-muted mb-2">Réservation au nom de <strong>{connecte.nom}</strong> ({connecte.email}) — complétez votre adresse.</p>
            ) : (
              <div className="row g-2 mb-2">
                <div className="col-6"><label className="form-label">Prénom *</label><input name="prenom" className="form-control" required /></div>
                <div className="col-6"><label className="form-label">Nom *</label><input name="nom" className="form-control" required /></div>
                <div className="col-12"><label className="form-label">Courriel *</label><input name="email" type="email" className="form-control" required /></div>
              </div>
            )}
            <div className="row g-2">
              <div className="col-12"><label className="form-label">Adresse *</label><input name="adresse" className="form-control" required /></div>
              <div className="col-4"><label className="form-label">NPA</label><input name="npa" className="form-control" /></div>
              <div className="col-8"><label className="form-label">Localité</label><input name="localite" className="form-control" /></div>
              <div className="col-6"><label className="form-label">Téléphone{connecte ? '' : ' *'}</label>
                <input name="telephone" type="tel" className="form-control" defaultValue={connecte?.telephone || ''} required={!connecte} /></div>
              <div className="col-6"><label className="form-label">Personnes</label><input name="nb" type="number" min="1" className="form-control" /></div>
              <div className="col-12">
                <label className="form-label d-block">Clé du club-house *</label>
                <div className="adlt-choix">
                  <button type="button" className={`btn ${cleEtat === 'besoin' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setCleEtat('besoin')}>J'ai besoin d'une clé</button>
                  <button type="button" className={`btn ${cleEtat === 'possede' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setCleEtat('possede')}>Je possède déjà une clé</button>
                </div>
              </div>
              <div className="col-12"><label className="form-label">Motif</label><input name="motif" className="form-control" placeholder="Anniversaire, fête de famille…" /></div>
            </div>
          </div></div>
        </div>
      </div>
      {erreur && <div className="alert alert-danger py-2 mt-3">{erreur}</div>}
      <button className="btn btn-primary mt-3" disabled={etat === 'envoi'}>
        {etat === 'envoi' ? 'Envoi…' : 'Envoyer la demande de location'}
      </button>
      <p className="text-muted small mt-2">La demande est soumise à la validation du comité ; vous recevrez un courriel de décision.</p>
    </form>
  );
}

/** Demande publique d'animation ou d'initiation. */
export function DemandePublique({ type }) {
  const [etat, setEtat] = useState('saisie');
  const [erreur, setErreur] = useState('');
  const [tarifs, setTarifs] = useState(null);
  const [bloquees, setBloquees] = useState(new Set());
  const [date, setDate] = useState(null);
  const [lieuType, setLieuType] = useState('carolet');
  const [nb, setNb] = useState('');
  const cfg = window.ADLT_CONFIG || {};
  const connecte = !!cfg.loggedIn;
  const libelle = type === 'initiation' ? 'initiation au tir à l\'arc' : 'animation';
  useEffect(() => {
    api('demandes/tarifs').then((r) => r.ok && setTarifs(r.data));
    if (type === 'animation') {
      const du = new Date().toISOString().slice(0, 10);
      const au = new Date(Date.now() + 120 * 86400000).toISOString().slice(0, 10);
      api(`lieux/0/disponibilites?du=${du}&au=${au}`).then((r) => r.ok && setBloquees(new Set(r.data.bloquees)));
    }
  }, [type]);

  async function submit(e) {
    e.preventDefault();
    const f = e.target;
    // La validation doit précéder le passage en « envoi » : sortir après
    // setEtat('envoi') laissait le bouton désactivé jusqu'au rechargement.
    if (type === 'animation' && !date) { setErreur('Choisissez une date disponible dans le calendrier.'); return; }
    setEtat('envoi'); setErreur('');
    const r = await api('demandes', {
      method: 'POST',
      body: {
        type, date_souhaitee: type === 'animation' ? date : f.date?.value, heure_souhaitee: f.heure.value,
        nb_personnes: f.nb.value,
        lieu_type: lieuType, lieu_autre: f.lieu_autre?.value || '',
        prenom: f.prenom?.value, nom: f.nom?.value, email: f.email?.value,
        telephone: f.telephone.value, adresse: f.adresse.value,
        npa: f.npa.value, localite: f.localite.value,
        message: f.message.value, site_web: f.site_web.value,
      },
    });
    if (r.ok) setEtat('envoye');
    else { setEtat('saisie'); setErreur(r.error); }
  }

  if (etat === 'envoye') {
    return <div className="alert alert-success">Merci ! Votre demande d'{libelle} a bien été transmise au comité, qui vous répondra par courriel.</div>;
  }
  return (
    <form onSubmit={submit}>
      <h2 className="h4">Demande d'{libelle}</h2>
      {type === 'initiation' && tarifs && (
        <div className="alert alert-light border py-2">
          Tarifs pour les trois cours : jeune <strong>{fmtCHF(tarifs.jeune)}</strong> · adulte <strong>{fmtCHF(tarifs.adulte)}</strong> par personne.
        </div>
      )}
      {erreur && <div className="alert alert-danger py-2">{erreur}</div>}
      <input name="site_web" tabIndex="-1" autoComplete="off" style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true" />
      <div className="row g-4">
      {type === 'animation' && (
        <div className="col-lg-5 order-lg-2">
          <div className="card adlt-etape h-100"><div className="card-body">
            <h5>Date souhaitée *</h5>
            <MiniCalendrier bloquees={lieuType === 'carolet' ? bloquees : new Set()} selected={date} onSelect={setDate}
              legende="En violet : dates disponibles · en gris : dates occupées." />
            {date && <div className="alert alert-primary py-2 mt-2 mb-0">Date choisie : <strong>{fmtDate(date)}</strong></div>}
          </div></div>
        </div>
      )}
      <div className={type === 'animation' ? 'col-lg-7 order-lg-1' : 'col-12'}>
      <div className="card adlt-etape"><div className="card-body">
      <div className="row g-3">
      {type !== 'animation' && (
        <div className="col-md-4"><label className="form-label">Date souhaitée *</label><input name="date" type="date" className="form-control" required /></div>
      )}
      <div className="col-md-4"><label className="form-label">Heure souhaitée</label><input name="heure" type="time" className="form-control" />
        {type === 'animation' && <small className="text-muted">L'animation dure 90 minutes.</small>}</div>
      <div className="col-md-4"><label className="form-label">Nombre de personnes *</label>
        <input name="nb" type="number" min={type === 'animation' ? (tarifs?.animation_min_personnes || 4) : 1} className="form-control" required value={nb} onChange={(e) => setNb(e.target.value)} />
        {type === 'animation' && <small className="text-muted">Minimum {tarifs?.animation_min_personnes || 4} participants.</small>}</div>
      {type === 'animation' && tarifs && (
        <div className="col-md-4 d-flex align-items-end">
          <div className="alert alert-light border py-2 mb-0 w-100">
            {fmtCHF(tarifs.animation_par_personne)} / personne
            {nb > 0 && <> — soit <strong>{fmtCHF(tarifs.animation_par_personne * nb)}</strong></>}
          </div>
        </div>
      )}
      {type === 'animation' && (
        <div className="col-12">
          <label className="form-label d-block">Lieu de l'animation *</label>
          <div className="adlt-choix">
            <button type="button" className={`btn ${lieuType === 'carolet' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setLieuType('carolet')}>Terrain du Carolet</button>
            <button type="button" className={`btn ${lieuType === 'autre' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setLieuType('autre')}>Autre lieu</button>
          </div>
          {lieuType === 'carolet' && <small className="text-muted d-block mt-1">Réserve automatiquement le club-house.</small>}
          {lieuType === 'autre' && <input name="lieu_autre" className="form-control mt-2" placeholder="Précisez le lieu *" required />}
        </div>
      )}
      {!connecte && (
        <>
          <div className="col-md-6"><label className="form-label">Prénom *</label><input name="prenom" className="form-control" required /></div>
          <div className="col-md-6"><label className="form-label">Nom *</label><input name="nom" className="form-control" required /></div>
          <div className="col-md-6"><label className="form-label">Courriel *</label><input name="email" type="email" className="form-control" required /></div>
        </>
      )}
      <div className={connecte ? 'col-md-6' : 'col-md-6'}><label className="form-label">Téléphone *</label><input name="telephone" type="tel" className="form-control" required /></div>
      <div className="col-12"><label className="form-label">Adresse</label><input name="adresse" className="form-control" /></div>
      <div className="col-md-4"><label className="form-label">NPA</label><input name="npa" className="form-control" /></div>
      <div className="col-md-8"><label className="form-label">Localité</label><input name="localite" className="form-control" /></div>
      <div className="col-12"><label className="form-label">Message</label><textarea name="message" rows="3" className="form-control" placeholder="Contexte, âge des participants, attentes…"></textarea></div>
      </div>
      </div></div>
      </div>
      </div>
      {type === 'animation' && (
        <p className="text-muted small mt-3 mb-1">La demande d'animation est faite sous réserve de la disponibilité des moniteurs.</p>
      )}
      <button className="btn btn-primary mt-2" disabled={etat === 'envoi'}>{etat === 'envoi' ? 'Envoi…' : 'Envoyer la demande'}</button>
    </form>
  );
}

export function DemandeAnimation() { return <DemandePublique type="animation" />; }

/** Inscription à une initiation : trois cours à choisir dans le calendrier. */
export function DemandeInitiation() {
  const [creneaux, setCreneaux] = useState(null);
  const [tarifs, setTarifs] = useState(null);
  const [choisis, setChoisis] = useState([]); // [{serie_id, date, ...}]
  const [dateActive, setDateActive] = useState(null);
  const [etat, setEtat] = useState('saisie');
  const [erreur, setErreur] = useState('');
  const cfg = window.ADLT_CONFIG || {};
  const connecte = !!cfg.loggedIn;

  useEffect(() => {
    api('initiations/creneaux').then((r) => setCreneaux(r.ok ? r.data : []));
    api('demandes/tarifs').then((r) => r.ok && setTarifs(r.data));
  }, []);

  if (creneaux === null) return <Spinner />;

  const cle = (o) => o.serie_id + '|' + o.date;
  const estChoisi = (o) => choisis.some((c) => cle(c) === cle(o));
  function basculer(o) {
    if (estChoisi(o)) setChoisis(choisis.filter((c) => cle(c) !== cle(o)));
    else if (choisis.length < 3) setChoisis([...choisis, o]);
    else setErreur('Trois cours au maximum : désélectionnez-en un pour en changer.');
  }

  async function submit(e) {
    e.preventDefault();
    const f = e.target;
    if (choisis.length !== 3) { setErreur('Choisissez exactement trois cours.'); return; }
    setErreur(''); setEtat('envoi');
    const r = await api('demandes', {
      method: 'POST',
      body: {
        type: 'initiation',
        cours: choisis.map((c) => ({ serie_id: c.serie_id, date: c.date })),
        nb_personnes: 1, // inscription individuelle
        prenom: f.prenom?.value, nom: f.nom?.value, email: f.email?.value,
        telephone: f.telephone.value, adresse: f.adresse.value,
        npa: f.npa.value, localite: f.localite.value,
        message: f.message.value, consentement: f.consentement.checked ? 1 : 0,
        site_web: f.site_web.value,
      },
    });
    if (r.ok) setEtat('envoye');
    else { setEtat('saisie'); setErreur(r.error); }
  }

  if (etat === 'envoye') {
    return <div className="alert alert-success">Merci ! Votre inscription aux trois cours d'initiation a bien été transmise.
      Vous recevrez un courriel dès la décision du responsable.</div>;
  }

  // Regroupe les occurrences par date pour l'affichage calendrier.
  const parDate = {};
  creneaux.forEach((o) => { (parDate[o.date] = parDate[o.date] || []).push(o); });

  return (
    <form onSubmit={submit}>
      <h2 className="h4 mb-2">Inscription aux cours débutants</h2>
      <p className="text-muted">L'inscription est <strong>individuelle</strong> et porte sur <strong>trois cours</strong>, à choisir ci-dessous (dates différentes possibles).</p>
      {tarifs && (
        <div className="alert alert-light border py-2">
          Tarifs <strong>pour les trois cours</strong> : jeune (jusqu'à {tarifs.age_jeune_max} ans) <strong>{fmtCHF(tarifs.jeune)}</strong> · adulte <strong>{fmtCHF(tarifs.adulte)}</strong>.
        </div>
      )}
      <input name="site_web" tabIndex="-1" autoComplete="off" style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true" />

      <Etape n="1" titre={<>Choisissez vos trois cours <span className="badge bg-primary ms-1">{choisis.length}/3</span></>}>
        {creneaux.length === 0 && <p className="text-muted mb-0">Aucun cours proposé pour le moment — revenez prochainement.</p>}
        <div className="row g-3">
          <div className="col-md-6 order-md-2">
            <MiniCalendrier
              disponibles={new Set(Object.keys(parDate))}
              multi
              selected={new Set(choisis.map((c) => c.date))}
              onSelect={(iso) => {
                const occs = parDate[iso] || [];
                setDateActive(iso);
                if (!occs.length) return;
                // Un cours de cette date est déjà choisi : le tap le retire.
                const dejaChoisi = occs.find((o) => estChoisi(o));
                if (dejaChoisi) { basculer(dejaChoisi); return; }
                // Un seul horaire : sélection directe ; plusieurs : choix affiché à côté.
                if (occs.length === 1) basculer(occs[0]);
              }}
              legende="En violet : dates avec cours proposé — touchez trois dates pour composer vos trois cours." />
          </div>
          <div className="col-md-6 order-md-1">
            {dateActive && parDate[dateActive] ? (
              <>
                <strong className="d-block mb-1">{fmtDate(dateActive)}</strong>
                {parDate[dateActive].length > 1 && <small className="text-muted d-block mb-1">Plusieurs horaires ce jour-là — choisissez :</small>}
                {parDate[dateActive].map((o) => (
                  <button type="button" key={cle(o)}
                    className={`btn btn-sm me-2 mt-1 ${estChoisi(o) ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => basculer(o)}>
                    {o.heure_debut}–{o.heure_fin}{o.lieu ? ' · ' + o.lieu : ''}{estChoisi(o) ? ' ✓' : ''}
                  </button>
                ))}
              </>
            ) : <p className="text-muted">Sélectionnez une date dans le calendrier.</p>}
            {choisis.length > 0 && (
              <div className="alert alert-primary py-2 mt-3 mb-0">
                <strong>Mes trois cours :</strong>
                {choisis.map((c) => <div key={cle(c)}>{fmtDate(c.date)} — {c.heure_debut}–{c.heure_fin}{c.lieu ? ' · ' + c.lieu : ''}</div>)}
              </div>
            )}
          </div>
        </div>
      </Etape>

      <Etape n="2" titre="Vos coordonnées">
        <div className="row g-2">
          {!connecte && (
            <>
              <div className="col-md-6"><label className="form-label">Prénom *</label><input name="prenom" className="form-control" required /></div>
              <div className="col-md-6"><label className="form-label">Nom *</label><input name="nom" className="form-control" required /></div>
              <div className="col-md-6"><label className="form-label">Courriel *</label><input name="email" type="email" className="form-control" required /></div>
            </>
          )}
          <div className="col-md-6"><label className="form-label">Téléphone *</label><input name="telephone" type="tel" className="form-control" required /></div>
          <div className="col-12"><label className="form-label">Adresse *</label><input name="adresse" className="form-control" required /></div>
          <div className="col-md-4"><label className="form-label">NPA *</label><input name="npa" className="form-control" required /></div>
          <div className="col-md-8"><label className="form-label">Localité *</label><input name="localite" className="form-control" required /></div>
          <div className="col-12"><label className="form-label">Message</label><textarea name="message" rows="2" className="form-control"></textarea></div>
        </div>
        <div className="form-check mt-3">
          <input className="form-check-input" type="checkbox" name="consentement" id="init-consent" required />
          <label className="form-check-label" htmlFor="init-consent">
            Je consens au traitement de mes données personnelles par la Compagnie des Archers de la Tour aux fins de l'organisation des cours débutants. *
          </label>
        </div>
      </Etape>

      {erreur && <div className="alert alert-danger py-2">{erreur}</div>}
      <button className="btn btn-primary" disabled={etat === 'envoi' || choisis.length !== 3}>
        {etat === 'envoi' ? 'Envoi…' : 'Envoyer mon inscription (3 cours)'}
      </button>
    </form>
  );
}

/** Recherche de moniteurs — libellé court et loupe. */
function BoutonMoniteur({ onClick }) {
  return (
    <button className="btn btn-outline-primary btn-sm d-flex align-items-center justify-content-center gap-1 w-100"
      title="Demander des moniteurs" onClick={onClick}>
      <IconeLoupe /> Moniteur
    </button>
  );
}

/** Modifier — icône seule (cible tactile, libellé en infobulle). */
function BoutonModifier({ onClick }) {
  return (
    <button className="btn btn-info btn-sm w-100 d-flex align-items-center justify-content-center"
      title="Modifier" aria-label="Modifier" onClick={onClick}>
      <IconeCrayon />
    </button>
  );
}

/** Supprimer — pleine largeur de sa colonne. */
function BoutonSupprimer({ onClick }) {
  return (
    <button className="btn btn-danger btn-sm w-100 d-flex align-items-center justify-content-center"
      title="Supprimer" aria-label="Supprimer" onClick={onClick}>
      <IconeCorbeille />
    </button>
  );
}

/** Écran comité : valider, refuser ou demander des moniteurs. */
export function Demandes({ toast, type }) {
  const [state, reload] = useApi('demandes', { deps: [] });
  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;
  const lignes = type ? state.data.filter((d) => d.type === type) : state.data;
  const titre = type === 'animation' ? 'Gestion des animations'
    : type === 'initiation' ? 'Gestion des cours débutants'
    : 'Animations et cours débutants';

  async function decider(id, statut) {
    const commentaire = prompt(statut === 'validee' ? 'Message au demandeur (facultatif) :' : 'Motif du refus (facultatif) :') ?? '';
    const r = await api(`demandes/${id}/decision`, { method: 'POST', body: { statut, commentaire } });
    if (r.ok) { toast('Décision enregistrée, demandeur notifié.'); reload(); } else toast(r.error, true);
  }

  async function rechercher(id) {
    const n = prompt('Nombre de moniteurs nécessaires pour cette animation :', '1');
    if (n === null) return;
    const requis = Math.max(1, parseInt(n, 10) || 1);
    const r = await api(`demandes/${id}/recherche-moniteurs`, { method: 'POST', body: { moniteurs_requis: requis } });
    if (r.ok) { toast(`Recherche lancée (${requis} moniteur·s requis) : moniteurs notifiés, demandeur informé.`); reload(); } else toast(r.error, true);
  }

  async function modifier(d) {
    const date = prompt('Date (AAAA-MM-JJ) :', d.date_souhaitee);
    if (date === null) return;
    const heure = prompt('Heure (HH:MM, vide = inchangée) :', d.heure_souhaitee || '');
    if (heure === null) return;
    const nb = prompt('Nombre de personnes :', d.nb_personnes || '');
    if (nb === null) return;
    const r = await api('demandes/' + d.id, {
      method: 'PATCH',
      body: { date_souhaitee: date, heure_souhaitee: heure, nb_personnes: nb },
    });
    if (r.ok) { toast('Demande modifiée.'); reload(); } else toast(r.error, true);
  }

  async function supprimer(id) {
    if (!confirm('Supprimer définitivement cette demande ?')) return;
    const r = await api('demandes/' + id, { method: 'DELETE' });
    if (r.ok) { toast('Demande supprimée.'); reload(); } else toast(r.error, true);
  }

  async function basculerClubhouse(d, actif) {
    const r = await api('demandes/' + d.id, { method: 'PATCH', body: { bloque_clubhouse: actif ? 1 : 0 } });
    if (r.ok) { toast(actif ? 'Le club-house est bloqué pour cette date.' : 'Le club-house est libéré pour cette date.'); reload(); }
    else toast(r.error, true);
  }

  async function ajouter(e) {
    e.preventDefault();
    const f = e.target;
    const r = await api('demandes', {
      method: 'POST',
      body: {
        type: type || f.type?.value || 'animation',
        date_souhaitee: f.date.value, heure_souhaitee: f.heure.value,
        nb_personnes: f.nb.value, prenom: f.prenom.value, nom: f.nom.value,
        email: f.email.value, telephone: f.telephone.value, message: f.message.value,
        // Cours débutants : validé d'office ; animation : à traiter (valider,
        // refuser ou demander des moniteurs).
        statut: type === 'initiation' ? 'validee' : '',
      },
    });
    if (r.ok) { toast(type === 'initiation' ? 'Ajouté et validé.' : 'Animation ajoutée — validez-la ou lancez la recherche de moniteurs.'); f.reset(); reload(); } else toast(r.error, true);
  }

  const TYPES = { animation: 'Animation', initiation: 'Cours débutants' };
  return (
    <div>
      <h2 className="h4 mb-2">{titre}</h2>
      <Intro>Traitez les demandes reçues : validez, refusez, lancez une recherche de
        moniteurs ou ajoutez une demande manuellement.</Intro>
      <details className="card mb-3">
        <summary className="card-header" style={{ cursor: 'pointer' }}>+ Ajouter {type === 'initiation' ? 'une inscription aux cours débutants' : 'une animation'}</summary>
        <form onSubmit={ajouter} className="card-body row g-2">
          <div className="col-md-3"><label className="form-label">Date *</label><input name="date" type="date" className="form-control" required /></div>
          <div className="col-md-2"><label className="form-label">Heure</label><input name="heure" type="time" className="form-control" /></div>
          <div className="col-md-2"><label className="form-label">Personnes</label><input name="nb" type="number" min="1" className="form-control" /></div>
          <div className="col-md-5"><label className="form-label">Téléphone</label><input name="telephone" className="form-control" /></div>
          <div className="col-md-4"><label className="form-label">Prénom *</label><input name="prenom" className="form-control" required /></div>
          <div className="col-md-4"><label className="form-label">Nom *</label><input name="nom" className="form-control" required /></div>
          <div className="col-md-4"><label className="form-label">Courriel *</label><input name="email" type="email" className="form-control" required /></div>
          <div className="col-12"><label className="form-label">Message</label><input name="message" className="form-control" /></div>
          <div className="col-12"><button className="btn btn-primary btn-sm">{type === 'initiation' ? 'Ajouter (validée d\'office)' : 'Ajouter la demande d\'animation'}</button></div>
        </form>
      </details>
      {lignes.length === 0 && <p className="text-muted">Aucune demande.</p>}
      <div className="row g-3">
        {lignes.map((d) => (
          <div className="col-lg-6" key={d.id}>
            <div className="card h-100"><div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <h5 className="mb-1">{TYPES[d.type]} — {fmtDate(d.date_souhaitee)}</h5>
                <Badge statut={d.statut} />
              </div>
              <p className="text-muted mb-1">
                {d.heure_souhaitee ? d.heure_souhaitee + ' · ' : ''}{d.nb_personnes ? d.nb_personnes + ' pers. · ' : ''}reçue le {fmtDate(d.cree_le)}
              </p>
              <p className="mb-1"><strong>{d.prenom} {d.nom}</strong><br />
                <small>{d.email}{d.telephone ? ' · ' + d.telephone : ''}{d.adresse ? ' · ' + d.adresse : ''}</small></p>
              {d.cours && d.cours.length > 0 && (
                <p className="mb-1"><strong>Cours :</strong><br />
                  {d.cours.map((c, i) => <small key={i} className="d-block">{fmtDate(c.date)} · {c.heure_debut}–{c.heure_fin}{c.lieu ? ' · ' + c.lieu : ''}</small>)}
                </p>
              )}
              {d.consentement_le && <p className="mb-1"><small className="text-muted">Consentement donné (horodaté).</small></p>}
              {d.message && <p className="mb-2"><small>{d.message}</small></p>}
              <p className="mb-1"><small className="text-muted">Lieu : {d.lieu || 'Terrain du Carolet'}</small></p>
              {d.type === 'animation' && (
                <div className="form-check form-switch mb-2">
                  <input className="form-check-input" type="checkbox" id={'bloque-ch-' + d.id}
                    checked={!!d.bloque_clubhouse}
                    onChange={(e) => basculerClubhouse(d, e.target.checked)} />
                  <label className="form-check-label" htmlFor={'bloque-ch-' + d.id}>
                    <small>Bloque le club-house à cette date</small>
                  </label>
                </div>
              )}
              {d.recherche_moniteurs && <p className="mb-2">
                <span className={`badge ${d.moniteurs_confirmes ? 'bg-success' : 'bg-info text-dark'}`}>
                  {d.moniteurs_confirmes ? 'Moniteurs trouvés ✓' : `Recherche de moniteurs (${d.moniteurs_requis || 1} requis)`}
                </span>
                {d.activite_id && <a className="ms-2" href={'#/activite/' + d.activite_id}>voir l'activité →</a>}
                {d.moniteurs_inscrits && d.moniteurs_inscrits.length > 0 && (
                  <><br /><small className="text-muted">Moniteurs : {d.moniteurs_inscrits.join(', ')}</small></>
                )}</p>}
              {/* Décision : deux moitiés ; actions : trois tiers égaux. */}
              {d.statut === 'demandee' && (
                <div className="row g-2 mb-2">
                  <div className="col-6 d-grid">
                    <button className="btn btn-success btn-sm" onClick={() => decider(d.id, 'validee')}>✓ Valider</button>
                  </div>
                  <div className="col-6 d-grid">
                    <button className="btn btn-outline-danger btn-sm" onClick={() => decider(d.id, 'refusee')}>✗ Refuser</button>
                  </div>
                </div>
              )}
              <div className="row g-2">
                <div className="col-4">
                  {d.recherche_moniteurs
                    ? <button className="btn btn-outline-secondary btn-sm w-100" disabled title="Recherche déjà lancée">Moniteur</button>
                    : <BoutonMoniteur onClick={() => rechercher(d.id)} />}
                </div>
                <div className="col-4"><BoutonModifier onClick={() => modifier(d)} /></div>
                <div className="col-4"><BoutonSupprimer onClick={() => supprimer(d.id)} /></div>
              </div>
            </div></div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Une ligne d'animation (à venir ou archivée). */
function LigneAnimation({ d, onOpenActivite, passee }) {
  return (
    <div className={`list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2 ${passee ? 'text-muted' : ''}`}>
      <span>
        <strong>{fmtDate(d.date)}</strong>{d.heure ? ' à ' + d.heure : ''}
        {d.nb_personnes ? ` · ${d.nb_personnes} personne(s)` : ''}{d.lieu ? ` · ${d.lieu}` : ''}
        {d.demandeur && <><br /><small className="text-muted">Demandeur : {d.demandeur}</small></>}
      </span>
      {!passee && d.recherche_moniteurs && d.activite_id && (
        <button className="btn btn-primary btn-sm"
          onClick={() => (onOpenActivite ? onOpenActivite(d.activite_id) : (location.hash = '#/activite/' + d.activite_id))}>
          S'inscrire comme moniteur
        </button>
      )}
    </div>
  );
}

/**
 * Mes Animations : les prestations que le membre encadre (il s'est annoncé
 * comme moniteur), avec leur statut. Les passées suivent, atténuées.
 */
export function MesAnimations({ onOpenActivite }) {
  const [state] = useApi('mes-animations', { cache: 'mes_animations' });
  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;
  const TYPES_D = { animation: 'Animation', initiation: 'Cours débutants' };
  const aVenir = state.data.filter((d) => !d.passee);
  const passees = state.data.filter((d) => d.passee);
  const ligne = (d) => (
    <div key={d.id} className={`list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2 ${d.passee ? 'text-muted' : ''}`}>
      <span>
        <strong>{TYPES_D[d.type] || d.type}</strong> — {fmtDate(d.date)}{d.heure ? ' à ' + d.heure : ''}
        {d.nb_personnes ? ` · ${d.nb_personnes} personne(s)` : ''}{d.lieu ? ` · ${d.lieu}` : ''}
        {d.demandeur && <><br /><small className="text-muted">Demandeur : {d.demandeur}</small></>}
      </span>
      <span className="d-flex align-items-center gap-2">
        <Badge statut={d.statut} />
        {!d.passee && d.activite_id && (
          <button className="btn btn-outline-primary btn-sm"
            onClick={() => (onOpenActivite ? onOpenActivite(d.activite_id) : (location.hash = '#/activite/' + d.activite_id))}>
            Voir l'activité
          </button>
        )}
      </span>
    </div>
  );
  return (
    <div>
      <h2 className="h4 mb-2">Mes Animations</h2>
      <Intro>Les animations et cours débutants pour lesquels vous vous êtes annoncé(e)
        comme moniteur, avec leur date et leur statut.</Intro>
      <OfflineNote result={state} />
      {aVenir.length === 0 && <p className="text-muted">Vous n'êtes inscrit(e) comme moniteur à aucune animation à venir.</p>}
      <div className="list-group">{aVenir.map(ligne)}</div>
      {passees.length > 0 && (
        <details className="mt-4">
          <summary className="h6 mb-2" style={{ cursor: 'pointer' }}>
            Animations passées <span className="badge bg-secondary ms-1">{passees.length}</span>
          </summary>
          <div className="list-group mt-2">{passees.map(ligne)}</div>
        </details>
      )}
    </div>
  );
}

/**
 * Liste des animations validées (membres) — bouton d'inscription moniteur.
 * Les animations passées sont conservées dans une archive dépliable.
 */
export function ListeAnimations({ onOpenActivite }) {
  const [state] = useApi('agenda-demandes?type=animation', { cache: 'agenda_animations' });
  const [archive] = useApi('agenda-demandes?type=animation&archive=1', { cache: 'agenda_animations_archive' });
  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;
  const passees = archive.ok ? archive.data : [];
  return (
    <div>
      <h2 className="h4 mb-2">Animations</h2>
      <Intro>Les animations organisées par le club pour des groupes externes.
        Annoncez-vous comme moniteur lorsqu'un encadrement est recherché.</Intro>
      {state.data.length === 0 && <p className="text-muted">Aucune animation planifiée.</p>}
      <div className="list-group">
        {state.data.map((d) => <LigneAnimation key={d.id} d={d} onOpenActivite={onOpenActivite} />)}
      </div>

      {passees.length > 0 && (
        <details className="mt-4">
          <summary className="h6 mb-2" style={{ cursor: 'pointer' }}>
            Archive — animations passées <span className="badge bg-secondary ms-1">{passees.length}</span>
          </summary>
          <div className="list-group mt-2">
            {passees.map((d) => <LigneAnimation key={d.id} d={d} passee />)}
          </div>
        </details>
      )}
    </div>
  );
}

/** Tableau des cours débutants (à venir ou archivés). */
function TableauInitiations({ lignes, onOpenActivite, passees }) {
  return (
    <div className="table-responsive">
      <table className={`table align-middle ${passees ? 'text-muted' : ''}`}>
        <thead><tr><th>Date du cours</th><th>Heure</th><th>Personne</th><th>Participants</th><th></th></tr></thead>
        <tbody>
          {lignes.map((d) => (
            <tr key={d.id}>
              <td>{fmtDate(d.date)}</td>
              <td>{d.heure || '—'}</td>
              <td>{d.demandeur || '—'}</td>
              <td>{d.nb_personnes || '—'}</td>
              <td>{!passees && d.recherche_moniteurs && d.activite_id && (
                <button className="btn btn-outline-primary btn-sm"
                  onClick={() => (onOpenActivite ? onOpenActivite(d.activite_id) : (location.hash = '#/activite/' + d.activite_id))}>
                  S'inscrire
                </button>
              )}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Liste des initiations futures (moniteurs) : identité et dates des cours. */
export function ListeInitiations({ onOpenActivite }) {
  const [state] = useApi('agenda-demandes?type=initiation', { cache: 'agenda_initiations' });
  const [archive] = useApi('agenda-demandes?type=initiation&archive=1', { cache: 'agenda_initiations_archive' });
  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;
  const passees = archive.ok ? archive.data : [];
  return (
    <div>
      <h2 className="h4 mb-2">Liste des cours débutants</h2>
      <Intro>Les cours débutants planifiés, avec les participants annoncés.
        Inscrivez-vous sur les cours qui recherchent un moniteur.</Intro>
      {state.data.length === 0 && <p className="text-muted">Aucun cours débutant planifié.</p>}
      {state.data.length > 0 && <TableauInitiations lignes={state.data} onOpenActivite={onOpenActivite} />}

      {passees.length > 0 && (
        <details className="mt-4">
          <summary className="h6 mb-2" style={{ cursor: 'pointer' }}>
            Archive — cours passés <span className="badge bg-secondary ms-1">{passees.length}</span>
          </summary>
          <div className="mt-2"><TableauInitiations lignes={passees} passees /></div>
        </details>
      )}
    </div>
  );
}
